import React, { useCallback } from "react";
import {
  getStatusIcon,
  getStatusNumberIcon,
} from "../../../common/statusIcons";
import { validateArrayIncludes } from "../../../common/validators";
import { getStatusNumberText } from "../../../utils/orders";

// APP
import OrderActions from "./OrderActions";
// MATERIAL UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LightDarkGreyBox } from "../../styled";
import { StyledDarkGreyBadge } from "../../styled/badge";
import { OrderStatusIconButton } from "../../styled/button";
import { grey, orange, red } from "@mui/material/colors";
import { selectFirstOrderIndex } from "../../../store/actions/ordersSlice";
import { useSelector } from "react-redux";

export default function CashierOrderRow({
  item,
  handleOrderModal,
  changeMapCenter,
  acceptOrder,
  handleSelectDriver,
  readyToPickupOrder,
  finishOrder,
  cancelDriverRequest,
  packedOrder,
  extraDelivery,
}) {
  const biggestOrderIndex = useSelector(selectFirstOrderIndex);

  const enablePackedControl = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.config.enable_packed_control
  );

  const handleOrderModalClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      handleOrderModal(item, e);
    },
    [handleOrderModal]
  );

  const acceptOrderClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      acceptOrder(item, e);
    },
    [acceptOrder]
  );

  const handleSelectDriverClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      handleSelectDriver(item, e);
    },
    [handleSelectDriver]
  );

  const readyToPickupOrderClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      readyToPickupOrder(item, e);
    },
    [readyToPickupOrder]
  );

  const finishOrderClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      finishOrder(item, e);
    },
    [finishOrder]
  );

  const cancelDriverRequestClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      cancelDriverRequest(item, e);
    },
    [cancelDriverRequest]
  );

  const packedOrderClick = useCallback(
    (item, e) => {
      e.stopPropagation();
      packedOrder(item, e);
    },
    [finishOrder]
  );

  const getOrderIndexWidth = useCallback(() => {
    switch (biggestOrderIndex?.toString().length) {
      case 1:
        return 50;
      case 2:
        return 75;
      case 3:
        return 100;

      default:
        return 50;
    }
  }, [biggestOrderIndex]);

  return (
    <>
      <LightDarkGreyBox
        onClick={(e) => {
          handleOrderModalClick(item, e);
        }}
        sx={{
          p: 1,
          mb: 1,
          borderRadius: 2,
          "&:hover": {
            cursor: "pointer",
            backgroundColor: grey[200],
          },
          ...(item.status_number === 0 && {
            // Define keyframes for background color transition
            "@keyframes backgroundColorPalette": {
              "0%": { background: "#bce3ff" },
              "25%": { background: "#7ccdff" },
              "50%": { background: "#61a5fd" },
              "75%": { background: "#7caaff" },
              "100%": { background: "#bce3ff" },
            },
            // Apply the animation conditionally
            animation: "backgroundColorPalette 2s infinite alternate linear",
          }),
          ...(item.lateStatus &&
            validateArrayIncludes([2, 7], item.status_number) && {
              backgroundColor: red[50],
            }),
          ...(item.isAlmostLate &&
            validateArrayIncludes([2, 7], item.status_number) && {
              backgroundColor: orange[50],
            }),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          style={
            validateArrayIncludes([1, 2, 8, 13, 21], item.status_number) &&
            item.pickup_code
              ? { backgroundColor: "#c2c1f9" }
              : null
          }
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <StyledDarkGreyBadge>
              <Typography variant="body2" align="center" fontWeight={700}>
                {item.order_index}
              </Typography>
            </StyledDarkGreyBadge>
            {item.order_type === "Interior" ||
              (item.order_type === "INDOOR" && (
                <Box>
                  <Typography variant="body2">
                    {item.merchant} - {item.indoor_table}
                  </Typography>
                </Box>
              ))}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems={"center"}
            justifyContent={"end"}
            sx={{ margin: 0, minWidth: "25%" }}
          >
            <Box
              title={item.status}
              sx={{
                position: "relative",
                padding: 0,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  boxShadow: item.has_extra_delivery
                    ? "0px 4px 6px rgba(0, 0, 0, 0.5)"
                    : null,
                  borderRadius: "50%",
                }}
              >
                <Tooltip title={getStatusNumberText(item.status_number)}>
                  <OrderStatusIconButton statusNumber={item.status_number}>
                    {item.status_number
                      ? getStatusNumberIcon(item.status_number)
                      : getStatusIcon(item.status)}
                  </OrderStatusIconButton>
                </Tooltip>
              </Box>

              {item.has_extra_delivery && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: "75%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                >
                  <Tooltip title={"Corrida Extra"}>
                    <OrderStatusIconButton statusNumber={5}>
                      {getStatusNumberIcon(5)}
                    </OrderStatusIconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <StyledDarkGreyBadge
              bgColor={grey["300"]}
              display="flex"
              sx={{
                display: "flex",
                justifyItems: "start",
              }}
            >
              <OrderActions
                item={item}
                acceptOrder={acceptOrderClick}
                readyToPickupOrder={readyToPickupOrderClick}
                finishOrder={finishOrderClick}
                _handleModal={handleSelectDriverClick}
                cancelDriverRequest={cancelDriverRequestClick}
                packed={packedOrderClick}
                enablePacked={enablePackedControl}
              />
            </StyledDarkGreyBadge>
            <StyledDarkGreyBadge
              sx={{
                width: getOrderIndexWidth(),
              }}
            >
              <Typography variant="body2" align="center" fontWeight={700}>
                {item.order_index}
              </Typography>
            </StyledDarkGreyBadge>
          </Stack>
        </Stack>
      </LightDarkGreyBox>
    </>
  );
}
